/**
 * Adds the given perseus library version information to the __perseus_debug__
 * object and ensures that the object is attached to `globalThis` (`window` in
 * browser environments).
 *
 * This allows each library to provide runtime version information to assist in
 * debugging in production environments.
 */
const addLibraryVersionToPerseusDebug = (libraryName, libraryVersion) => {
  // If the library version is the default value, then we don't want to
  // prefix it with a "v" to indicate that it is a version number.
  let prefix = "v";
  if (libraryVersion === "__lib_version__") {
    prefix = "";
  }
  const formattedVersion = `${prefix}${libraryVersion}`;
  if (typeof globalThis !== "undefined") {
    var _globalThis$__perseus;
    globalThis.__perseus_debug__ = (_globalThis$__perseus = globalThis.__perseus_debug__) != null ? _globalThis$__perseus : {};
    const existingVersionEntry = globalThis.__perseus_debug__[libraryName];
    if (existingVersionEntry) {
      // If we already have an entry and it doesn't match the registered
      // version, we morph the entry into an array and log a warning.
      if (existingVersionEntry !== formattedVersion) {
        // Existing entry might be an array already (oops, at least 2
        // versions of the library already loaded!).
        const allVersions = Array.isArray(existingVersionEntry) ? existingVersionEntry : [existingVersionEntry];
        allVersions.push(formattedVersion);
        globalThis.__perseus_debug__[libraryName] = allVersions;

        // eslint-disable-next-line no-console
        console.warn(`Multiple versions of ${libraryName} loaded on this page: ${allVersions.sort().join(", ")}`);
      }
    } else {
      globalThis.__perseus_debug__[libraryName] = formattedVersion;
    }
  } else {
    // eslint-disable-next-line no-console
    console.warn(`globalThis not found found (${formattedVersion})`);
  }
};

// This file is processed by a Rollup plugin (replace) to inject the production
const libName = "@khanacademy/perseus-core";
const libVersion = "1.5.0";
addLibraryVersionToPerseusDebug(libName, libVersion);

/**
 * @typedef {Object} Errors utility for referencing the Perseus error taxonomy.
 */
const Errors = Object.freeze({
  /**
   * @property {ErrorKind} Unknown The kind of error is not known.
   */
  Unknown: "Unknown",
  /**
   * @property {ErrorKind} Internal The error is internal to the executing code.
   */
  Internal: "Internal",
  /**
   * @property {ErrorKind} InvalidInput There was a problem with the provided
   * input, such as the wrong format or a null value.
   */
  InvalidInput: "InvalidInput",
  /**
   * @property {ErrorKind} NotAllowed There was a problem due to the state of
   * the system not matching the requested operation or input. For example,
   * trying to create a username that is valid, but is already taken by
   * another user. Use {@link InvalidInput} instead when the input isn't
   * valid regardless of the state of the system. Use {@link NotFound} when
   * the failure is due to not being able to find a resource.
   */
  NotAllowed: "NotAllowed",
  /**
   * @property {ErrorKind} TransientService There was a problem when making a
   * request to a service.
   */
  TransientService: "TransientService",
  /**
   * @property {ErrorKind} Service There was a non-transient problem when
   * making a request to service.
   */
  Service: "Service"
});

/**
 * @type {ErrorKind} The kind of error being reported
 */

class PerseusError extends Error {
  constructor(message, kind, options) {
    super(message);
    this.kind = void 0;
    this.metadata = void 0;
    this.kind = kind;
    this.metadata = options == null ? void 0 : options.metadata;
  }
}

export { Errors, PerseusError, addLibraryVersionToPerseusDebug, libVersion };
//# sourceMappingURL=index.js.map
